import { HydraWorkspace } from 'cio/services/hydra';

export enum ProductApp {
  JOURNEYS = 'journeys',
  DATA_PIPELINES = 'pipelines',
}

export const productAppWorkspaceInitializedFields: Record<ProductApp, keyof HydraWorkspace> = {
  [ProductApp.DATA_PIPELINES]: 'cdp_initialized',
  [ProductApp.JOURNEYS]: 'cio_initialized',
};

export function isProductReady(product: ProductApp, workspace: HydraWorkspace) {
  let fieldName = productAppWorkspaceInitializedFields[product];
  if (fieldName == null) {
    throw new Error(`No "initialized" field defined for product: ${product}`);
  }

  let isInitialized = workspace[fieldName];

  if (typeof isInitialized !== 'boolean') {
    // Fall back to false if the field is not a boolean - in case the API
    // returns something unexpected or the payload shape changes
    return false;
  }

  return isInitialized;
}

export type ProductAppInfo = {
  name: string;
  header: string;
  steps: string[];
  platformQuestion?: string;
  platformOtherText?: string;
  platformOptions?: {
    value: string;
    label: string;
    icon: string;
  }[];
  goalsQuestion: string;
  goalsOptions: {
    value: string;
    label: string;
  }[];
  employeeCountQuestion?: string;
  employeeCountOptions?: {
    value: string;
    label: string;
  }[];
  formValues: {
    platform?: string;
    platformOther?: string;
    useCase: string;
    useCaseDetail: string;
    employeeCount?: string;
  };
  primaryMessaginGoalQuestion?: string;
  primaryMessaginGoalOptions?: {
    value: string;
    label: string;
  }[];
  roleQuestion?: string;
  roleOptions?: {
    value: string;
    label: string;
  }[];
  automationExperienceQuestion?: string;
  automationExperienceOptions?: {
    value: string;
    label: string;
  }[];
  channelsQuestion?: string;
  channelsOptions?: {
    value: string;
    label: string;
  }[];
  integrationExperienceQuestion?: string;
  integrationExperienceOptions?: {
    value: string;
    label: string;
  }[];
};

export const productActivationInfo: Record<ProductApp, ProductAppInfo> = {
  [ProductApp.DATA_PIPELINES]: {
    name: ProductApp.DATA_PIPELINES,
    header: 'Set up Data Pipelines to work for your business.',
    steps: ['Experience', 'Goals'],
    platformQuestion: 'Do you use a Customer Data Platform (CDP) today?',
    platformOtherText: 'CDP name',
    platformOptions: [
      {
        value: 'segment',
        label: 'Segment',
        icon: new URL('cio/assets/images/cdp-platforms/segment.svg', import.meta.url).toString(),
      },
      {
        value: 'rudderstack',
        label: 'Rudderstack',
        icon: new URL('cio/assets/images/cdp-platforms/rudderstack.svg', import.meta.url).toString(),
      },
      {
        value: 'other',
        label: 'Other',
        icon: new URL('cio/assets/images/cdp-platforms/other.svg', import.meta.url).toString(),
      },
      {
        value: 'none',
        label: 'None',
        icon: new URL('cio/assets/images/cdp-platforms/none.svg', import.meta.url).toString(),
      },
    ],
    goalsQuestion: 'My primary goal is:',
    goalsOptions: [
      { value: 'costs', label: 'Reduce current CDP costs' },
      { value: 'integration', label: 'Reduce integration effort' },
      { value: 'data_quality', label: 'Improve our data quality' },
      { value: 'checking_out', label: 'Just check it out' },
    ],
    formValues: {
      platform: '',
      platformOther: '',
      useCase: '',
      useCaseDetail: '',
    },
  },
  [ProductApp.JOURNEYS]: {
    name: ProductApp.JOURNEYS,
    header: 'Set up Journeys to work for your business.',
    steps: ['Experience', 'Goals'],
    goalsQuestion: 'What is your primary messaging goal?',
    goalsOptions: [
      { value: 'onboard_or_retain', label: 'Onboard or retain customers' },
      { value: 'leads', label: 'Convert marketing leads' },
      { value: 'transactional', label: 'Send transactional messages' },
      { value: 'newsletters', label: 'Send newsletters' },
      { value: 'internal_automations', label: 'Set up internal automations' },
      { value: 'other', label: 'Other' },
    ],
    employeeCountQuestion: 'Number of employees',
    employeeCountOptions: [
      { value: '1 - 10', label: '1 – 10' },
      { value: '11 - 50', label: '11 – 50' },
      { value: '51 - 200', label: '51 – 200' },
      { value: '201 - 1,000', label: '201 – 1,000' },
      { value: '1,000+', label: '1,000+' },
    ],
    formValues: {
      useCase: '',
      useCaseDetail: '',
      employeeCount: '',
    },
    roleQuestion: 'What kind of work do you do?',
    roleOptions: [
      { value: 'marketing', label: 'Marketing' },
      { value: 'product', label: 'Product Management' },
      { value: 'sales_ops', label: 'Marketing/Sales Ops' },
      { value: 'sales', label: 'Sales' },
      { value: 'analytics', label: 'Analytics/Data Science' },
      { value: 'engineering', label: 'Engineering' },
      { value: 'operations', label: 'Operations' },
      { value: 'design', label: 'Design' },
      { value: 'ux', label: 'UX Research' },
      { value: 'other', label: 'Other' },
    ],
    channelsQuestion: 'Which channels do you use?',
    channelsOptions: [
      { value: 'email', label: 'Email' },
      { value: 'twilio', label: 'SMS' },
      { value: 'push', label: 'Push Notifications' },
      { value: 'in_app', label: 'In-App Messages' },
      { value: 'slack', label: 'Slack' },
      { value: 'other', label: 'Other' },
    ],
    automationExperienceQuestion: 'Have you ever used a messaging automation tool before?',
    automationExperienceOptions: [
      { value: 'beginner', label: "No, it's my first time using a messaging automation tool" },
      { value: 'experienced', label: "Yes, I've used them a few times" },
      { value: 'cio_experienced', label: "Yes, and I've even used Customer.io before ❤" },
    ],
    integrationExperienceQuestion: 'Will you be the one connecting your data to Customer.io?',
    integrationExperienceOptions: [
      { value: 'experienced_implementer', label: 'Yes and I feel pretty confident setting it up' },
      { value: 'beginner_implementer', label: "Yes, but I'll need you to walk me through it" },
      { value: 'non_implementer', label: 'No, a teammate will help me with that' },
    ],
  },
};
